import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, CheckCircle2, Loader2, Mail, Lock, Users } from 'lucide-react';

export function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginAsParent } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');

  // Validate invite on mount
  useEffect(() => {
    const validateInvite = async () => {
      try {
        const response = await fetch(`${API}/auth/invite/${token}`);
        const data = await response.json();
        
        if (data.ok) {
          setInviteData(data);
          setError(null);
        } else {
          setError(data.error || 'Invalid invite link');
          setErrorCode(data.error_code);
        }
      } catch (err) {
        setError('Unable to validate invite. Please try again.');
        setErrorCode('NETWORK_ERROR');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      validateInvite();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validation
    if (!email || !password) {
      setFormError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API}/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password })
      });

      const data = await response.json();

      if (data.ok) {
        // Use auth context to store session
        loginAsParent(data.access_token, data.family_id, data.parent_id);
        
        // Redirect to onboarding
        navigate(`/parent/onboarding/${data.family_id}`);
      } else {
        if (data.error_code === 'EXISTING_ACCOUNT') {
          setFormError('An account with this email already exists. Please login instead.');
        } else {
          setFormError(data.error || 'Failed to create account');
        }
      }
    } catch (err) {
      setFormError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100" data-testid="invite-loading">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-4" />
            <p className="text-gray-600">Validating invite...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100" data-testid="invite-error">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-red-800">
              {errorCode === 'EXPIRED' && 'Invite Expired'}
              {errorCode === 'ALREADY_USED' && 'Invite Already Used'}
              {errorCode === 'INVALID' && 'Invalid Invite'}
              {!['EXPIRED', 'ALREADY_USED', 'INVALID'].includes(errorCode) && 'Error'}
            </CardTitle>
            <CardDescription className="text-red-600">
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-gray-600 mb-4">
              {errorCode === 'EXPIRED' && 'Please ask the operator to send you a new invite link.'}
              {errorCode === 'ALREADY_USED' && 'This invite has already been used. If you already have an account, please login.'}
              {errorCode === 'INVALID' && 'The invite link is invalid. Please check the link or request a new one.'}
            </p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              data-testid="invite-error-home-btn"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid invite - show signup form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4" data-testid="invite-accept-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
            <Users className="h-6 w-6 text-indigo-600" />
          </div>
          <CardTitle className="text-xl">Join {inviteData?.family_name || 'Your Family'}</CardTitle>
          <CardDescription>
            Create your parent account to get started with Kivo
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="invite-email-input"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="invite-password-input"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  data-testid="invite-confirm-password-input"
                  disabled={submitting}
                />
              </div>
            </div>

            {formError && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2" data-testid="invite-form-error">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={submitting}
              data-testid="invite-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating Account...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Join Family
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Invite expires: {inviteData?.expires_at ? new Date(inviteData.expires_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
